import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  Avatar,
  alpha
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import PetsIcon from '@mui/icons-material/Pets';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import Page from "../../components/Page";
import axios from "../../utils/axios";

const statusColorMap = {
  PRODUCTION: "success",
  SUSPENDED: "error",
  PAUSED: "warning",
  ONBOARDING: "info",
  EVALUATING: "default",
  DATA_ACTIVE: "primary",
  DATA_INACTIVE: "secondary",
  CAMPAIGN_ACTIVE: "success",
  CAMPAIGN_INACTIVE: "default",
};

const StatCard = ({ icon, title, value, color }) => (
  <Card sx={{ 
    height: '100%',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
    },
    transition: 'all 0.3s ease-in-out'
  }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ 
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          color: 'primary.main'
        }}>
          {icon}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default function PracticeDetail() {
  const { practiceId } = useParams();
  const navigate = useNavigate();
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPractice = async () => {
      try {
        const response = await axios.get(`/campaign/getPracticeDetails?practiceId=${practiceId}`);
        if (response.data) {
          setPractice(response.data);
        } else {
          setError("Practice not found");
        }
      } catch (err) {
        console.error("Error fetching practice:", err);
        setError("Failed to load practice details");
      } finally {
        setLoading(false);
      }
    };
    fetchPractice();
  }, [practiceId]);

  if (loading) {
    return (
      <Page>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Page>
    );
  }

  if (!practice) {
    return null;
  }

  return (
    <Page>
      <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4, px: 2 }}>
        {/* Header Section */}
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3, 
            position: 'relative', 
            overflow: 'hidden',
            bgcolor: 'background.paper',
            boxShadow: (theme) => theme.customShadows.z8
          }}
        >
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            width: '100%', 
            height: '100%', 
            background: (theme) => `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            zIndex: 0 
          }} />
          <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Tooltip title="Back to list">
              <IconButton 
                onClick={() => navigate('/dashboard/practices')}
                sx={{ 
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                {practice.name}
              </Typography>
              <Chip
                label={practice.status}
                color={statusColorMap[practice.status]}
                variant="outlined"
                size="small"
                sx={{ 
                  borderColor: (theme) => alpha(theme.palette[statusColorMap[practice.status]].main, 0.48),
                  color: (theme) => theme.palette[statusColorMap[practice.status]].main
                }}
              />
            </Box>
            <Tooltip title="Edit Practice">
              <IconButton 
                color="primary"
                sx={{ 
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                  }
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Stats Section */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <StatCard
              icon={<PeopleIcon />}
              title="Total Clients"
              value={practice.totalClients?.toLocaleString()}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              icon={<PetsIcon />}
              title="Total Patients"
              value={practice.totalPatients?.toLocaleString()}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              icon={<CalendarViewMonthIcon />}
              title="Last Transaction"
              value={new Date(practice.lastTransactionDate).toLocaleDateString()}
              color="primary"
            />
          </Grid>
        </Grid>

        {/* Practice Information Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              bgcolor: 'background.paper',
              boxShadow: (theme) => theme.customShadows.z8
            }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ 
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main'
                  }}>
                    <BusinessIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Practice Information
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {practice.address1}
                      {practice.address2 && <br />}{practice.address2}
                      <br />
                      {practice.city}, {practice.state} {practice.zip}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {practice.phone}
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {practice.website}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ 
              bgcolor: 'background.paper',
              boxShadow: (theme) => theme.customShadows.z8
            }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Avatar sx={{ 
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    color: 'primary.main'
                  }}>
                    <PeopleIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Primary Contact
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {practice.primaryContactFirstName} {practice.primaryContactLastName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Contact Details
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {practice.primaryContactEmail}
                    </Typography>
                    <Typography variant="body1" color="text.primary">
                      {practice.primaryContactPhone}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
} 